import { ChevronDownIcon } from "@chakra-ui/icons";
import { Menu, MenuButton, Button, MenuList, MenuItem } from "@chakra-ui/react";
import React from "react";
import { scrollbarStyles } from "../../../theme/scrollbarStyles";

export type MenuDropdownOption = {
  value: string;
  label: string;
};

export type ChakraMenuDropdownVariant = "outline" | "ghost";

export function Dropdown({
  value,
  options,
  onChange,
  isDisabled,
  ariaLabel,
  placeholder = "Select option",
  variant = "outline",
}: {
  value: string | undefined;
  options: MenuDropdownOption[];
  onChange: (value: string) => void;
  isDisabled?: boolean;
  ariaLabel?: string;
  placeholder?: string;
  variant?: ChakraMenuDropdownVariant;
}) {
  const selectedOption = options.find((option) => option.value === value);
  const isPlaceholder = !selectedOption;

  const menuListRef = React.useRef<HTMLDivElement | null>(null);
  const itemRefs = React.useRef<Record<string, HTMLButtonElement | null>>({});

  const setItemRef = React.useCallback(
    (optionValue: string) => (node: HTMLButtonElement | null) => {
      itemRefs.current[optionValue] = node;
    },
    [],
  );

  return (
    <Menu matchWidth placement="bottom-start" isLazy autoSelect={false}>
      {({ isOpen }) => (
        <>
          <FocusSelectedItem
            isOpen={isOpen}
            value={value}
            itemRefs={itemRefs}
            menuListRef={menuListRef}
          />

          <MenuButton
            as={Button}
            size="sm"
            variant={variant}
            rightIcon={<ChevronDownIcon boxSize={3} />}
            isDisabled={isDisabled}
            aria-label={ariaLabel}
            color={isPlaceholder ? "gray.500" : "gray.700"}
            fontWeight={isPlaceholder ? "normal" : "medium"}
            px={3}
            py={4}
          >
            {selectedOption?.label ?? placeholder}
          </MenuButton>

          <MenuList
            ref={menuListRef}
            maxH="16rem"
            overflowY="auto"
            minW={0}
            py={1}
            zIndex="popover"
            css={scrollbarStyles.none}
          >
            {options.map((option) => {
              const isSelected = option.value === value;

              return (
                <MenuItem
                  key={option.value}
                  ref={setItemRef(option.value)}
                  fontWeight={isSelected ? "medium" : "normal"}
                  fontSize="small"
                  bg={isSelected ? "gray.50" : undefined}
                  onClick={() => onChange(option.value)}
                  px={4}
                  py={2}
                >
                  {option.label}
                </MenuItem>
              );
            })}
          </MenuList>
        </>
      )}
    </Menu>
  );
}

function FocusSelectedItem({
  isOpen,
  value,
  itemRefs,
  menuListRef,
}: {
  isOpen: boolean;
  value: string | undefined;
  itemRefs: React.MutableRefObject<Record<string, HTMLButtonElement | null>>;
  menuListRef: React.RefObject<HTMLDivElement | null>;
}) {
  React.useEffect(() => {
    if (!isOpen || !value) {
      return;
    }

    const selectedItem = itemRefs.current[value];
    const menuList = menuListRef.current;

    if (!selectedItem || !menuList) {
      return;
    }

    let raf1 = 0;
    let raf2 = 0;

    // Wait until Chakra has mounted/positioned the menu and finished its own focus work.
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        const item = itemRefs.current[value];
        const list = menuListRef.current;

        if (!item || !list) {
          return;
        }

        // Keep the page from jumping.
        item.focus({ preventScroll: true });

        const itemTop = item.offsetTop;
        const listHeight = list.clientHeight;

        let nextScrollTop = itemTop;

        const maxScrollTop = list.scrollHeight - listHeight;
        nextScrollTop = Math.max(0, Math.min(nextScrollTop, maxScrollTop));

        list.scrollTop = nextScrollTop;
      });
    });

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [isOpen, value, itemRefs, menuListRef]);

  return null;
}
